import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleThunkError } from '../common/util';
import { ConfigName, updateConfigObject } from '../sqlite/common/config';
import { RootState } from '.';
import { setIsLoadingCountState } from './appSlice';
import { logConsole, logTransaction } from '../common/log';
import { LogType } from '../models/log.model';
import { IMediaSettings, IMediaSettingsSyncStatus } from '../models/dms/systemConfiguration.model';
import { getMediaSettings } from '../api/dms/mediaAttachment.api';

interface IMediaSettingsSyncState {
  mediaSettingsSync?: IMediaSettingsSyncStatus;
  mediaSettingsSyncLoading: boolean;
}

const initialState: IMediaSettingsSyncState = {
  mediaSettingsSync: {
    hasSync: false,
    timestamp: 0,
    isProcessing: false,
    data: {} as IMediaSettings,
  },
  mediaSettingsSyncLoading: false,
};

/// THUNKS

export const setMediaSettingsSyncStoreAsync = createAsyncThunk<{}, IMediaSettingsSyncStatus | undefined>('mediaSettingsSync/setMediaSettingsSyncStoreAsync', async (val, { rejectWithValue, dispatch }) => {
  logTransaction(LogType.Info, `Setting media settings to storage`, '[mediaSettingsSync] setMediaSettingsSyncStoreAsync');
  try {
    dispatch(setMediaSettingsSyncState(val));
    await updateConfigObject<IMediaSettingsSyncStatus>(ConfigName.MediaSettingsSync, val || ({} as IMediaSettingsSyncStatus), false);
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setMediaSettingsProcessingStoreAsync = createAsyncThunk<{}, boolean>('mediaSettingsSync/setMediaSettingsProcessingAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[mediaSettingsSync] setMediaSettingsProcessingAsync');
  try {
    dispatch(setMediaSettingsProcessingState(val));
    await updateConfigObject<IMediaSettingsSyncStatus>(
      ConfigName.MediaSettingsSync,
      {
        ...(getState() as RootState).mediaSettingsSync.mediaSettingsSync,
        isProcessing: val,
      } as IMediaSettingsSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const setMediaSettingsHasSyncStoreAsync = createAsyncThunk<{}, boolean>('mediaSettingsSync/setMediaSettingsHasSyncStoreAsync', async (val, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, '', '[mediaSettingsSync] setMediaSettingsHasSyncStoreAsync');
  try {
    dispatch(setMediaSettingsHasSyncState(val));
    await updateConfigObject<IMediaSettingsSyncStatus>(
      ConfigName.MediaSettingsSync,
      {
        ...(getState() as RootState).mediaSettingsSync.mediaSettingsSync,
        hasSync: val,
        isProcessing: false,
      } as IMediaSettingsSyncStatus,
      false,
    );
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue);
  }
});

export const fetchMediaSettingsStoreAsync = createAsyncThunk<{}, void>('mediaSettingsSync/fetchMediaSettingsStoreAsync', async (nil, { rejectWithValue, dispatch, getState }) => {
  logTransaction(LogType.Info, `Fetching media settings from database`, '[mediaSettingsSync] fetchMediaSettingsStoreAsync');
  try {
    dispatch(setIsLoadingCountState(1));
    dispatch(setMediaSettingsSyncLoadingState(true));

    const state = getState() as RootState;
    const { mediaSettingsSync } = state.mediaSettingsSync;

    let mediaSettingsSyncState = { ...mediaSettingsSync } as IMediaSettingsSyncStatus;

    try {
      const responseData = await getMediaSettings();

      mediaSettingsSyncState = {
        ...mediaSettingsSyncState,
        timestamp: new Date().getTime(),
        hasSync: true,
        isProcessing: false,
        data: { ...responseData },
      };

      dispatch(setMediaSettingsSyncState(mediaSettingsSyncState));
      await updateConfigObject<IMediaSettingsSyncStatus>(ConfigName.MediaSettingsSync, mediaSettingsSyncState, false);
      return responseData;
    } catch (ex: any) {
      logTransaction(LogType.Error, `Failed to execute fetchMediaSettingsStoreAsync thunk. ${ex?.message}`);
    } finally {
      dispatch(setIsLoadingCountState(-1));
      dispatch(setMediaSettingsSyncLoadingState(false));
    }
  } catch (err: any) {
    return handleThunkError(err, rejectWithValue, setIsLoadingCountState(-1), setMediaSettingsSyncLoadingState(false));
  }
});

/// SLICE

export const mediaSettingsSyncSlice = createSlice({
  name: 'mediaSettingsSync',
  initialState,
  reducers: {
    setMediaSettingsSyncState: (state, action: PayloadAction<IMediaSettingsSyncStatus | undefined>) => {
      logConsole(LogType.Info, '[mediaSettingsSync] setMediaSettingsSyncState');
      state.mediaSettingsSync = action.payload;
    },
    setMediaSettingsProcessingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[mediaSettingsSync] setMediaSettingsProcessingState: ${action.payload}`);
      if (state.mediaSettingsSync) {
        state.mediaSettingsSync.isProcessing = action.payload;
      }
    },
    setMediaSettingsHasSyncState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[mediaSettingsSync] setMediaSettingsHasSyncState: ${action.payload}`);
      if (state.mediaSettingsSync) {
        state.mediaSettingsSync.hasSync = action.payload;
        state.mediaSettingsSync.isProcessing = false;
      }
    },
    setMediaSettingsSyncLoadingState: (state, action: PayloadAction<boolean>) => {
      logConsole(LogType.Info, `[mediaSettingsSync] setMediaSettingsSyncLoadingState: ${action.payload}`);
      state.mediaSettingsSyncLoading = action.payload;
    },
  },
});

/// REDUCERS

export const { setMediaSettingsSyncState, setMediaSettingsProcessingState, setMediaSettingsHasSyncState, setMediaSettingsSyncLoadingState } = mediaSettingsSyncSlice.actions;

export default mediaSettingsSyncSlice.reducer;
